/**
 * Resolves the display name for a tenant's loyalty / rewards program.
 *
 * Each organisation can brand their loyalty program with their own name via
 * `loyalty_settings.program_name`. When that is not configured we fall back to
 * a sensible default so the UI never shows an empty label:
 *   - SaaS tenants: "<Company> Rewards" (or just "Rewards" when the company
 *     name is unknown).
 *   - The first-party (non-SaaS) Tools & Bubbles site keeps its historical
 *     "T&B Family Rewards" branding.
 */

/** Historical default used by the first-party Tools & Bubbles site. */
export const DEFAULT_LOYALTY_PROGRAM_NAME = 'T&B Family Rewards';

/** Generic fallback when no company name is available on a SaaS tenant. */
export const GENERIC_LOYALTY_PROGRAM_NAME = 'Rewards';

export interface ResolveLoyaltyProgramNameOptions {
  /** Configured per-org name from loyalty_settings.program_name. */
  programName?: string | null;
  /** Tenant company / brand name (used to derive a default on SaaS). */
  companyName?: string | null;
  /** Whether the app is running as a multi-tenant SaaS workspace. */
  isSaas?: boolean;
}

function normalizeLoyaltyLabel(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Detects loyalty program names auto-generated from a company / legal-entity label
 * (e.g. "ACME PRIVATE LIMITED Rewards") so the first-party T&B site keeps its
 * historical "T&B Family Rewards" branding unless a distinct custom name is set.
 */
export function isCompanyDerivedLoyaltyProgramName(
  programName: string | null | undefined,
  companyName?: string | null,
): boolean {
  const name = normalizeLoyaltyLabel(programName).toLowerCase();
  if (!name) return false;

  const company = normalizeLoyaltyLabel(companyName).toLowerCase();
  if (company) {
    if (name === company) return true;
    if (name === `${company} rewards`) return true;
  }

  if (/private limited rewards$/.test(name) || /\(opc\) private limited rewards$/.test(name)) {
    return true;
  }

  if (name.includes('tools and bubbles') && name.endsWith('rewards')) {
    return true;
  }

  return false;
}

/**
 * Returns the loyalty program name to display, honoring an explicit per-org
 * configuration first and falling back to brand-derived defaults.
 */
export function resolveLoyaltyProgramName(options: ResolveLoyaltyProgramNameOptions = {}): string {
  const { programName, companyName, isSaas = false } = options;

  const configured = normalizeLoyaltyLabel(programName);

  if (!isSaas) {
    if (configured && !isCompanyDerivedLoyaltyProgramName(configured, companyName)) {
      return configured;
    }
    return DEFAULT_LOYALTY_PROGRAM_NAME;
  }

  if (configured) return configured;

  const company = normalizeLoyaltyLabel(companyName);
  return company ? `${company} Rewards` : GENERIC_LOYALTY_PROGRAM_NAME;
}

/** Normalizes a program-name form value into the column value (null when blank). */
export function normalizeLoyaltyProgramNameInput(value: string | null | undefined): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed ? trimmed : null;
}
